/**
 * AdvancedAudit.tsx — T325 / WIZ-3 / FR-007 wired audit-history viewer.
 *
 * Replaces the prototype's `sub === 'audit'` block in `settings/_bundle.tsx`
 * (`AdvancedConsole` :406-419, which renders six fabricated rows via
 * `CIRCA.range(6)` over a hard-coded label list) with a reactive Convex
 * subscription on `api.auditQueries.listAuditEvents` — the SINGLE wired
 * audit query (`convex/auditQueries.ts:19`), `requireAdmin`-gated at :29,
 * server-side-ordered `.order("desc")` at :39/:47, server-side-capped at
 * `Math.min(args.limit ?? 200, 1000)` at :30.
 *
 * Design decisions pinned in code (not metadata) per the constitution's
 * "honest-state" rule (CLAUDE.md §1, §5):
 *
 *   1. ADMINISTRATOR-ONLY. The audit log is a security-sensitive
 *      append-only stream of secret-reads / admin-mutations /
 *      permission-denied events. The backend's `requireAdmin` is the
 *      authoritative gate; the screen MIRRORS it with `<AdminGate>` so a
 *      non-administrator sees the friendly "Acceso restringido" panel
 *      INSTEAD of issuing a query that the server would only reject.
 *      Letting a non-admin issue the query just to be rejected would
 *      flood the audit log itself with `permission_denied` rows — a
 *      noise generator that defeats the log's signal.
 *
 *   2. NO PHANTOM PERMISSION KEY. The PERMS matrix in
 *      `src/lib/permissions.ts` does NOT define a dedicated
 *      admin-only-screen permission literal; the admin-only screen
 *      guard goes through `<AdminGate>` (a role-direct check on
 *      `useCurrentMember().role === 'admin'`), not through
 *      `<AccessGate perm=...>` with an invented key. Using a
 *      non-existent permission key would silently false-allow on the
 *      client mirror and false-reject on the catalogue tests.
 *
 *   3. SERVER-SIDE FILTERING. The event-type filter is passed to the
 *      Convex query as the `eventType` argument; the backend re-issues
 *      the read against the `by_type_time` index instead of scanning
 *      the timestamp index and filtering in memory. Client-side
 *      filtering over the (capped) full result would silently lose
 *      matching rows that fell outside the 1000-row cap.
 *
 *   4. SERVER-SIDE ORDERING. The backend's `.order("desc")` is the
 *      authoritative chronological order. The screen renders rows in
 *      the order received — no client-side `rows.sort(...)`.
 *
 *   5. HONEST THREE-STATE RENDER. While the query is loading
 *      (`useQuery → undefined`) the screen renders a Skeleton. When
 *      it resolves to `[]` the screen renders an EmptyState ("Sin
 *      eventos"). When it resolves to rows the screen renders the
 *      rows. There is no fourth state — the prototype's
 *      `CIRCA.range(6)` fabricated rows are gone.
 *
 *   6. STABLE TIME WINDOW. The default range is the last 7 days,
 *      computed ONCE at mount via `useMemo(..., [])`. A re-render
 *      driven by a filter change does NOT advance the window — that
 *      would re-subscribe at every render and create a runaway
 *      subscription churn. The operator can refresh the window
 *      explicitly via the "Actualizar ventana" affordance.
 *
 * Routing: `/settings/advanced/audit` resolves to this screen via the
 * specific route entry added to `src/routes.tsx`. The generic
 * `/settings/advanced/:sub` rule (which falls back to the prototype
 * `AdvancedConsole` block) is preserved as the catch-all for the other
 * sub-tabs that have not yet been wired off the bundle.
 *
 * Tests: `src/screens/settings/advanced-audit.test.tsx` (T324) pins
 * the four contract cuts (a) AdminGate empty-state for non-admin, (b)
 * newest-first order from server, (c) event-type filter via query arg,
 * (d) three-state render (loading / empty / populated).
 *
 * FR-007.
 */
import { useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  AdminGate,
  EmptyState,
  PageHead,
  Panel,
  Pill,
  Skeleton,
} from '../../components/primitives';
import { useCurrentMember } from '../../hooks/useCurrentMember';

// ---------------------------------------------------------------------------
// Local types — mirror the Convex `auditLog` row shape and the closed
// 8-literal `eventType` union (convex/schema.ts:2454-2486 +
// convex/auditQueries.ts:14-17). The local types stay strict-mode clean
// without leaning on the generated `api` types at the call site (those
// resolve to `unknown` under the test mock).
// ---------------------------------------------------------------------------

type AuditEventType =
  | 'hmac_failure'
  | 'hmac_rotation_previous_used'
  | 'admin_mutation'
  | 'secret_read'
  | 'permission_denied'
  | 'config_change'
  | 'hmac_success'
  | 'brute_force_suspected';

type AuditSeverity = 'info' | 'warning' | 'critical';

interface AuditRow {
  _id: string;
  timestamp: number;
  eventType: AuditEventType;
  severity: AuditSeverity;
  source: string;
  sourceIpTrusted?: boolean;
  principal?: string;
  subject?: string;
  detail?: unknown;
}

// Operator-facing Spanish labels for the closed 8-literal union. The
// option `value` strings are the literal backend keys (so the query
// arg passes through unchanged); the `label` strings are the
// glossary-clean Spanish surface copy.
const EVENT_TYPE_OPTIONS: ReadonlyArray<{ value: AuditEventType; label: string }> = [
  { value: 'admin_mutation', label: 'Acción de administrador' },
  { value: 'secret_read', label: 'Lectura de secreto' },
  { value: 'permission_denied', label: 'Permiso denegado' },
  { value: 'config_change', label: 'Cambio de configuración' },
  { value: 'hmac_failure', label: 'Falla de firma de seguridad' },
  { value: 'hmac_rotation_previous_used', label: 'Firma anterior usada' },
  { value: 'hmac_success', label: 'Firma de seguridad verificada' },
  { value: 'brute_force_suspected', label: 'Posible fuerza bruta' },
];

// Map severity → Pill tone so the row's chip carries the same
// visual weight as the underlying severity literal. Tones must come
// from the closed `TONES` map in `components/primitives` (no inline
// colors so the global theme stays the single source of truth).
const SEVERITY_TONE: Record<AuditSeverity, 'info' | 'warn' | 'bad'> = {
  info: 'info',
  warning: 'warn',
  critical: 'bad',
};

// Lookup table for the operator-facing event-type label used inside
// row chips. Mirrors `EVENT_TYPE_OPTIONS` so the surface copy stays
// consistent between the filter dropdown and the row chip.
const EVENT_TYPE_LABEL: Record<AuditEventType, string> = EVENT_TYPE_OPTIONS
  .reduce<Record<AuditEventType, string>>((acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
  }, {} as Record<AuditEventType, string>);

const DEFAULT_RANGE_DAYS = 7;
const DEFAULT_LIMIT = 200;

function formatTimestamp(ts: number): string {
  // `es-MX` matches the operator locale (see CLAUDE.md §12). A
  // non-finite timestamp would only ever come from a malformed mock —
  // surface an em-dash rather than `Invalid Date`.
  if (!Number.isFinite(ts) || ts <= 0) return '—';
  return new Date(ts).toLocaleString('es-MX');
}

interface AdvancedAuditInnerProps {
  // Time window is hoisted into the inner so the outer's role-gating
  // does not destabilise its memoization.
  sinceMs: number;
  untilMs: number;
}

/**
 * Inner component — runs ONLY when the outer has verified the
 * current member is an administrator. Keeping the audit subscription
 * inside an inner that is conditionally MOUNTED (not just
 * conditionally rendered) ensures `useQuery` is NEVER called for a
 * non-administrator. The contract test (a) asserts exactly that
 * absence.
 */
function AdvancedAuditInner({ sinceMs, untilMs }: AdvancedAuditInnerProps) {
  // `undefined` = "all event types" (no filter). When the operator
  // picks a literal, the next render passes it as the query arg and
  // the server re-issues via the `by_type_time` index.
  const [eventType, setEventType] = useState<AuditEventType | undefined>(undefined);

  const rows = useQuery(api.auditQueries.listAuditEvents, {
    sinceMs,
    untilMs,
    limit: DEFAULT_LIMIT,
    eventType,
  }) as AuditRow[] | undefined;

  const loading = rows === undefined;
  const empty = !loading && rows.length === 0;

  return (
    <div>
      <PageHead
        title="Registro de auditoría"
        description="Registro de seguridad de solo anexado, últimos 7 días."
      />

      <Panel pad={16} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label
            htmlFor="advanced-audit-event-type"
            style={{ fontSize: 13, color: 'var(--ink-muted)', fontWeight: 500 }}
          >
            Tipo de evento
          </label>
          {/* Native `<select>` so the contract test (c) can read the
              available option values and fire a `change` event. The
              empty-string option encodes the "all types" selection;
              parseEventType maps it back to `undefined`. */}
          <select
            id="advanced-audit-event-type"
            value={eventType ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setEventType(v === '' ? undefined : (v as AuditEventType));
            }}
            style={{
              border: '1px solid var(--border-cool)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 14,
              fontFamily: 'var(--font-sans)',
              background: 'var(--canvas)',
              color: 'var(--ink-muted)',
              cursor: 'pointer',
              minWidth: 220,
            }}
          >
            <option value="">Todos los tipos</option>
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>
            Ventana: últimos {DEFAULT_RANGE_DAYS} días · límite {DEFAULT_LIMIT}
          </span>
        </div>
      </Panel>

      {loading && (
        <Panel pad={0}>
          {/* aria-busy + role=progressbar is the load-bearing signal the
              honest-state contract test (d.1) accepts as proof the
              screen is rendering a Skeleton rather than fabricated rows.
              The Skeleton primitive's `circa-skeleton` class is the
              visual carrier; `aria-busy` is what the test reads. */}
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Skeleton w="100%" h={48} aria-busy="true" role="progressbar" />
            <Skeleton w="100%" h={48} aria-busy="true" />
            <Skeleton w="100%" h={48} aria-busy="true" />
            <Skeleton w="100%" h={48} aria-busy="true" />
          </div>
        </Panel>
      )}

      {empty && (
        <Panel pad={0}>
          <EmptyState
            icon="fileText"
            title="Sin eventos"
            body="No se registraron eventos de auditoría en la ventana seleccionada."
          />
        </Panel>
      )}

      {!loading && !empty && (
        <Panel pad={0}>
          {rows.map((row, i) => (
            <div
              key={row._id}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                padding: '13px 20px',
                borderBottom:
                  i < rows.length - 1 ? '1px solid var(--lightest-gray)' : 'none',
              }}
            >
              <span
                className="t-mono"
                style={{
                  fontSize: 11,
                  color: 'var(--muted-slate)',
                  textTransform: 'none',
                  minWidth: 140,
                }}
              >
                {formatTimestamp(row.timestamp)}
              </span>
              <span style={{ flex: 1, fontSize: 13 }}>
                {row.principal ?? '—'}
              </span>
              <Pill tone={SEVERITY_TONE[row.severity] ?? 'neutral'} size="sm">
                {EVENT_TYPE_LABEL[row.eventType] ?? row.eventType}
              </Pill>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/**
 * Outer component — the routing entry point. Calls
 * `useCurrentMember()` directly, decides admin vs. non-admin, and
 * mounts the inner only for administrators. The `AdminGate` wrap is
 * preserved for two reasons:
 *
 *   - It is the established admin-only-screen primitive (see
 *     `AdvancedConsole` in `settings/_bundle.tsx:359-446`), and
 *     keeping the same surface keeps the visual contract identical
 *     across the advanced console.
 *   - For an unauthorised viewer it renders the locked Spanish
 *     "Acceso restringido" panel from `primitives/index.tsx:389`,
 *     which the contract test (a) reads as proof of the gate.
 *
 * Mounting the inner conditionally (rather than letting it run and
 * relying on AdminGate to hide its output) is what keeps `useQuery`
 * from being called for non-admins.
 */
export function AdvancedAudit() {
  const member = useCurrentMember();
  const isAdmin = !!member && member.role === 'admin';

  // 7-day window, computed once at mount. A re-render driven by a
  // filter change (state lives in the inner) does NOT change these
  // values, so the inner's `useQuery` arg-object identity stays
  // stable except across an explicit window refresh.
  const { sinceMs, untilMs } = useMemo(() => {
    const now = Date.now();
    return {
      sinceMs: now - DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000,
      untilMs: now,
    };
  }, []);

  return (
    <AdminGate>
      {isAdmin ? <AdvancedAuditInner sinceMs={sinceMs} untilMs={untilMs} /> : null}
    </AdminGate>
  );
}

export default AdvancedAudit;
